import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { UI, EASE } from '../../styles/tl5';
import { useGetContactInfoQuery } from '../../store/siteContentApi';
import PrescriptionUploadDialog from '../common/PrescriptionUploadDialog';
import CallbackRequestDialog from '../common/CallbackRequestDialog';

// The four-up contact band that sits directly under the hero.
//
// Two of these are plain links and work today. The other two — upload a
// prescription, request a callback — are the reference's own prototype
// behaviour: they flip to a confirmed state in local component state and
// send nothing anywhere. That is deliberate for this UI pass; both need a
// real endpoint before they can be trusted, and until then neither makes
// a promise the page can keep beyond what the operator already does by
// phone. See the note on each handler.

// Mirrors the reference's copy, used while ContactInfo is in flight and
// if the admin has not set a value.
const FALLBACK = {
  whatsapp: '+919014638633',
  phone: '1800 599 5656',
};

// U+FE0E forces text presentation. Without it iOS and most Android builds
// render ☎ as a full-colour emoji, which breaks the monochrome set.
const GLYPH = {
  phone: '☎︎',
  upload: '↑',
  callback: '↺',
};

const formatWhatsapp = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
  return raw || '';
};

// ContactInfo stores the toll-free line unspaced ("18005995656"), which
// reads as one long digit run. Group it the way the reference prints it.
const formatPhone = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1800')) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 5)} ${d.slice(5)}`;
  return raw || '';
};

const ContactBand = () => {
  const { data: contact } = useGetContactInfoQuery();
  // null = not sent yet; otherwise the server's reply, so the card can
  // say what actually happened rather than always claiming receipt.
  const [rx, setRx] = useState(null);
  // null = not sent yet; otherwise the server's reply, so the card can
  // quote the reference rather than just claiming success.
  const [callback, setCallback] = useState(null);
  const [cbOpen, setCbOpen] = useState(false);
  const [rxFile, setRxFile] = useState(null);
  const [rxOpen, setRxOpen] = useState(false);
  const fileRef = useRef(null);

  const whatsapp = contact?.whatsapp || FALLBACK.whatsapp;
  const phone = contact?.phone || FALLBACK.phone;
  const waDigits = String(whatsapp).replace(/\D/g, '');
  const telDigits = String(phone).replace(/\D/g, '');

  // Picking a file opens the dialog rather than submitting: the upload is
  // useless without a number to ring back on. The card only says
  // "received" once the server has actually taken it.
  const onFile = (e) => {
    const picked = e.target.files && e.target.files[0];
    if (!picked) return;
    setRxFile(picked);
    setRxOpen(true);
    // Let the same file be picked again after a cancel.
    e.target.value = '';
  };

  const card = {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    minWidth: 0,
    p: '16px 18px',
    borderRadius: '20px',
    bgcolor: '#FFFFFF',
    color: 'var(--tl-g9)',
    textDecoration: 'none',
    transition: `box-shadow .35s ${EASE}, transform .35s ${EASE}`,
    '&:hover': {
      boxShadow: '0 12px 30px -16px rgba(11,43,39,0.35)',
      color: 'var(--tl-g9)',
    },
  };

  const icon = {
    display: 'grid',
    placeItems: 'center',
    width: 44,
    height: 44,
    flexShrink: 0,
    borderRadius: '14px',
    fontFamily: UI,
    fontWeight: 800,
  };

  const title = { fontFamily: UI, fontSize: 16, fontWeight: 800, lineHeight: 1.25 };

  const sub = {
    fontFamily: UI,
    fontSize: 13,
    lineHeight: 1.4,
    color: 'var(--tl-muted-2)',
  };

  const stack = { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 };

  return (
    <Box component="section" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', pt: 1.5, px: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
          gap: 1,
        }}
      >
        <Box
          component="a"
          href={`https://wa.me/${waDigits}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={card}
        >
          <Box component="span" sx={{ ...icon, bgcolor: '#25D366', color: '#FFFFFF', fontSize: 13 }}>
            WA
          </Box>
          <Box sx={stack}>
            <Box component="span" sx={title}>Book on WhatsApp</Box>
            <Box component="span" sx={sub}>{formatWhatsapp(whatsapp)}</Box>
          </Box>
        </Box>

        <Box component="a" href={`tel:${telDigits}`} sx={card}>
          <Box component="span" sx={{ ...icon, bgcolor: 'var(--tl-g9)', color: '#FFFFFF', fontSize: 18 }}>
            {GLYPH.phone}
          </Box>
          <Box sx={stack}>
            <Box component="span" sx={title}>Book by call</Box>
            <Box component="span" sx={sub}>{`${formatPhone(phone)} · toll-free`}</Box>
          </Box>
        </Box>

        {/* A label wrapping a hidden input, so the whole card is the
            file picker's hit area without a nested button. */}
        <Box
          component="label"
          sx={{ ...card, bgcolor: 'var(--tl-yellow)', cursor: 'pointer', '&:hover': { ...card['&:hover'] } }}
        >
          <Box
            component="input"
            ref={fileRef}
            type="file"
            accept="image/*,.pdf"
            onChange={onFile}
            sx={{ display: 'none' }}
          />
          <Box component="span" sx={{ ...icon, bgcolor: 'var(--tl-g9)', color: 'var(--tl-yellow)', fontSize: 20 }}>
            {GLYPH.upload}
          </Box>
          <Box sx={stack}>
            <Box component="span" sx={title}>
              {!rx ? 'Upload prescription'
                : rx.files_received ? 'Prescription received ✓'
                  : 'We’ll call you ✓'}
            </Box>
            <Box component="span" sx={{ ...sub, color: 'var(--tl-g9)' }}>
              {!rx ? 'Photo or PDF. We book the tests for you'
                : rx.files_received ? 'We’ll call you within 15 minutes'
                  : `Reference ${rx.request_id} · keep the prescription handy`}
            </Box>
          </Box>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={() => setCbOpen(true)}
          sx={{ ...card, border: 0, textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
        >
          <Box component="span" sx={{ ...icon, bgcolor: 'var(--tl-mint)', color: 'var(--tl-teal-d)', fontSize: 18 }}>
            {GLYPH.callback}
          </Box>
          <Box sx={stack}>
            <Box component="span" sx={title}>
              {callback ? 'Callback requested ✓' : 'Get a callback'}
            </Box>
            <Box component="span" sx={sub}>
              {callback ? `Reference ${callback.request_id}` : 'Tell us when to call'}
            </Box>
          </Box>
        </Box>
      </Box>

      <CallbackRequestDialog
        open={cbOpen}
        source="home"
        onClose={() => setCbOpen(false)}
        onDone={(res) => setCallback(res)}
      />

      <PrescriptionUploadDialog
        open={rxOpen}
        file={rxFile}
        source="home"
        onClose={() => { setRxOpen(false); setRxFile(null); }}
        onDone={(res) => setRx(res)}
      />
    </Box>
  );
};

export default ContactBand;
